import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * The building blocks every tab of /admin/payments is made of, so each one
 * reads the same way (Sky, 2026-10-30: "organize and clean the action buttons
 * so they are in the same spot per tab"):
 *
 *   PayTabs        the tab row; a tab with something outstanding wears a red count,
 *                  and may carry a small tag saying how it's set ("Off", "Unsaved")
 *   SectionHeader  what this tab is (left) · its buttons (always right)
 *   SubTabs        the status filter (Open / Closed / …) with counts · Refresh at the right
 *   Attention      the callout that says how many items need a look, and where
 *   PayTable / Th / Td / ExpandToggle / ExpandedRow   one table style, Actions always last
 */

export interface PayTab<K extends string> {
  key: K;
  label: string;
  /** Items needing someone. Shown as a red count; 0 hides it. */
  alert?: number;
  /** A short state, not a problem: grey by default, amber for "off" or "unsaved". */
  tag?: { text: string; tone?: 'muted' | 'amber' };
}

export function PayTabs<K extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<PayTab<K>>;
  value: K;
  onChange: (k: K) => void;
}) {
  return (
    <div role="tablist" className="flex flex-wrap gap-1 border-b border-brix-border">
      {tabs.map((t) => {
        const active = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            className={cn(
              '-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold',
              active
                ? 'border-brix-primary text-brix-primary'
                : 'border-transparent text-brix-muted hover:text-brix-ink',
            )}
          >
            {t.label}
            {t.tag && (
              <span
                className={cn(
                  'rounded-full border px-1.5 text-[10px] font-semibold',
                  t.tag.tone === 'amber'
                    ? 'border-amber-200 bg-amber-50 text-amber-700'
                    : 'border-brix-border bg-brix-surface-alt text-brix-muted',
                )}
              >
                {t.tag.text}
              </span>
            )}
            {(t.alert ?? 0) > 0 && (
              <span
                className="inline-flex min-w-[20px] items-center justify-center rounded-full border border-red-200 bg-red-100 px-1.5 text-[11px] font-bold text-red-700"
                title={`${t.alert} need${t.alert === 1 ? 's' : ''} a look`}
              >
                {t.alert}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function SectionHeader({
  description,
  actions,
}: {
  description: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0 max-w-2xl flex-1 text-xs leading-relaxed text-brix-muted">{description}</div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end">{actions}</div>}
    </div>
  );
}

export interface SubTab<K extends string> {
  key: K;
  label: string;
  count?: number;
}

export function SubTabs<K extends string>({
  tabs,
  value,
  onChange,
  right,
}: {
  tabs: Array<SubTab<K>>;
  value: K;
  onChange: (k: K) => void;
  /** Usually the Refresh button. */
  right?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={cn(
            'rounded-full border px-3 py-1 text-xs font-semibold',
            value === t.key
              ? 'border-brix-primary bg-brix-primary text-white'
              : 'border-brix-border bg-white text-slate-700 hover:bg-brix-surface-alt',
          )}
        >
          {t.label}
          {t.count != null && <span className="ml-1 opacity-75">{t.count}</span>}
        </button>
      ))}
      {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
    </div>
  );
}

/** "3 need a look — …" when count > 0; nothing otherwise. */
export function Attention({ count, children }: { count: number; children: ReactNode }) {
  if (count <= 0) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-600" />
      <span>
        <span className="font-bold">
          {count} need{count === 1 ? 's' : ''} a look
        </span>{' '}
        — {children}
      </span>
    </div>
  );
}

/** Loading / error / empty in one place, so every tab says it the same way. */
export function TableState({
  loading,
  error,
  empty,
  emptyText,
  children,
}: {
  loading?: boolean;
  error?: unknown;
  empty?: boolean;
  emptyText: string;
  children: ReactNode;
}) {
  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-brix-muted">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </p>
    );
  }
  if (error) {
    return <p className="text-sm text-red-700">{error instanceof Error ? error.message : 'Failed to load'}</p>;
  }
  if (empty) {
    return (
      <p className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-brix-border px-3 py-6 text-sm text-brix-muted">
        <CheckCircle2 size={14} className="text-emerald-600" /> {emptyText}
      </p>
    );
  }
  return <>{children}</>;
}

export function PayTable({ children, minWidth = 760 }: { children: ReactNode; minWidth?: number }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-brix-border">
      <table className="w-full text-sm" style={{ minWidth }}>
        {children}
      </table>
    </div>
  );
}

export function Th({ children, right, className }: { children?: ReactNode; right?: boolean; className?: string }) {
  return (
    <th
      className={cn(
        'bg-brix-surface-alt/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-tight text-brix-muted',
        right ? 'text-right' : 'text-left',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  right,
  mono,
  className,
  colSpan,
}: {
  children?: ReactNode;
  right?: boolean;
  mono?: boolean;
  className?: string;
  colSpan?: number;
}) {
  return (
    <td
      colSpan={colSpan}
      className={cn('px-3 py-2 align-top', right && 'text-right', mono && 'font-mono tabular-nums', className)}
    >
      {children}
    </td>
  );
}

/** The Actions cell: always the last column, buttons right-aligned in one row. */
export function ActionsTd({ children }: { children: ReactNode }) {
  return (
    <td className="px-3 py-2 align-top">
      <div className="flex flex-wrap items-center justify-end gap-1.5">{children}</div>
    </td>
  );
}

export function ExpandToggle({ open, onClick, label }: { open: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-label={open ? `Hide ${label}` : `Show ${label}`}
      onClick={onClick}
      className="rounded p-1 text-brix-muted hover:bg-brix-surface-alt"
    >
      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
    </button>
  );
}

export function ExpandedRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr className="bg-brix-surface-alt/40">
      <td colSpan={colSpan} className="px-4 pb-3 pt-1">
        {children}
      </td>
    </tr>
  );
}
